import React, {useState, useEffect, useRef} from 'react';
import router from "next/router";
import {isEndlessInputValid, isTruthy, sendGtagEvent} from "@/lib/utils";
import ButtonGroup from "@/components/common/buttons/ButtonGroup";
import ReCAPTCHA from "react-google-recaptcha";
import { getPublicConfig } from '@/pages/api/config';
import toast from 'react-hot-toast';
import {ENDLESS_PROMPT_MAX, ENDLESS_PROMPT_MIN} from "@/lib/constants.mjs";
import { usePlayer } from "@/lib/usePlayer";
import { useChangePlayerGame } from "@/lib/usePlayerGame";

const UserPrompt = ({onSubmit, onQuit, invalidPrompt = false}) => {
	const { data: player } = usePlayer();
	const { mutateAsync } = useChangePlayerGame();

	const captchaEnabled = isTruthy(getPublicConfig('captchaEnabled'));
	const captchaKey = captchaEnabled ? getPublicConfig('captchaSiteKey') : null;
	const [prompt, setPrompt] = useState('')
	const [shake, setShake] = useState(false)
	const captchaRef = useRef(null);
	const timeout = useRef()

	useEffect(() => {
		if (invalidPrompt) {
			setShake(true);
			timeout.current = setTimeout(() => {
				setShake(false)
			}, 500)
		}

		return () => {
			clearTimeout(timeout.current)
		}
	}, [invalidPrompt])

	/* c8 ignore next 12 */
	const onCreatePlaylist = async (topic, uid, captchaToken) => {
		await mutateAsync({
			uid, 
			body: { topic, source: 'playlist_panel' }, 
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				...(captchaToken && { "x-captcha-token": captchaToken })
		}});
		sendGtagEvent({
			action: 'endless_prompt_submit',
			category: 'engagement',
			label: 'Endless Prompt Submit',
			value: 'click',
			user_id: uid,
			topic: topic,
		});
	}

	const onBegin = async (event) => {
		event.preventDefault();
		const topic = prompt.trim();
    if (!isEndlessInputValid(topic)) {
			if (!shake) {
				setShake(true)
				timeout.current = setTimeout(() => {
					setShake(false);
				}, 500)
			}
			return;
    }

    try {
			if (!captchaEnabled) {
				return onCreatePlaylist(topic, player?.uid);
			}

      const token = await captchaRef.current.executeAsync();
      if (!token) {
        return toast.error("Solve the reCAPTCHA challenge!");
      }

      onCreatePlaylist(topic, player?.uid, token);
    } catch (err) {
      toast.error(
        "Something went wrong while verifying the reCAPTCHA challenge. Please refresh the page and try again!"
      );
    }
  };

	const validInput = !prompt?.length || isEndlessInputValid(prompt) || !invalidPrompt;

	return (
		<form
			data-testid="infinite-topic-prompt"
			className={'flex flex-col flex-grow items-center w-[75%] max-w-[1040px] min-w-[360px] md:p-8 p-4 md:gap-6 gap-4'}
			onSubmit={onBegin}
		>
			<div className={'bg-white text-primary lg:text-xl md:text-lg text-sm rounded-lg bg-opacity-[85%] p-8'}>
				<div className={'md:text-2xl text-lg md:font-gBold font-gSemi'}>Let&apos;s pick a subject for your</div>
				<h1 data-testid="infinite-heading" className={'md:text-3xl text-2xl md:font-gBold font-gSemi'}>Infinite Pursuit!</h1>
				<div className={'font-gReg flex flex-col gap-4 py-4'}>
				  <div className={'text-lg'}>Enter a subject, topic, or theme you&apos;re passionate about to start your personalized trivia challenge.</div>
					<div><span className={'font-gBold'}>Examples:</span> Sports, Kangaroos, Quantum Mechanics, 90s Pop Music. From general to oddly specific, all topics are welcome!</div>
					<div className={'font-gBold'}>Please keep topics appropriate and respectful.</div>
				</div>
				<div className={`py-2 px-3 rounded-lg border-solid border-2 ${!shake && validInput ? 'border-transparent' : 'border-danger'} focus-within:border-violet-600 bg-white focus-within:bg-opacity-100 ${shake ? 'animate-shake' : ''}`}>
					<label htmlFor="prompt" className={'text-sm mb-1'}>Subject, topic, or theme:</label>
					<input
						type={'text'}
						id="prompt"
						data-testid="prompt"
						placeholder={'ex: 90s Pop Music'}
						className={'w-full bg-transparent placeholder:opacity-50 focus:outline-none'}
						value={prompt}
						minLength={ENDLESS_PROMPT_MIN}
						maxLength={ENDLESS_PROMPT_MAX}
						onChange={e => setPrompt(e.target.value)}
					/>
				</div>
			</div>
			<ButtonGroup
				primaryLabel={"Let's Play"}
				primaryAction={() => null}
				primaryType="submit"
				secondaryLabel={'Cancel'}
				secondaryAction={onQuit}
			/>
			{captchaEnabled && <ReCAPTCHA ref={captchaRef} sitekey={captchaKey} size="invisible" />}
		</form>
	);
};

export default UserPrompt;
